import { useState } from 'react';
import { api } from '../api';
import Logo from '../components/Logo';

const STEPS = [
  { num: 1, label: 'Contraseña admin',    key: 'admin' },
  { num: 2, label: 'Credenciales Wasabi', key: 'wasabi' },
  { num: 3, label: 'Empresa interna',     key: 'company' },
  { num: 4, label: 'Plan inicial',        key: 'plan' },
  { num: 5, label: 'Telegram',            key: 'telegram' },
  { num: 6, label: 'Completar',           key: 'complete' },
];

function Field({ label, type = 'text', value, onChange, placeholder }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 14px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none' }}
      />
    </div>
  );
}

function Btn({ onClick, disabled, loading, children, secondary }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{ background: secondary ? 'transparent' : 'var(--accent)', border: secondary ? '1px solid var(--border)' : 'none', color: secondary ? 'var(--muted)' : 'var(--bg)', padding: '10px 24px', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', cursor: (disabled || loading) ? 'not-allowed' : 'pointer', opacity: (disabled || loading) ? 0.5 : 1 }}
    >
      {loading ? 'PROCESANDO...' : children}
    </button>
  );
}

function Alert({ type, msg }) {
  if (!msg) return null;
  const colors = { error: 'var(--red)', success: 'var(--green)', info: 'var(--accent)' };
  return (
    <div style={{ padding: '10px 14px', marginBottom: 16, background: `rgba(0,0,0,0.2)`, border: `1px solid ${colors[type] || colors.info}`, color: colors[type] || colors.info, fontFamily: 'var(--mono)', fontSize: 12 }}>
      {msg}
    </div>
  );
}

export default function Setup({ onComplete }) {
  const [step, setStep]               = useState(0); // 0-indexed
  const [wizardToken, setWizardToken] = useState('');
  const [busy, setBusy]               = useState(false);
  const [alert, setAlert]             = useState(null);

  // Step 1 — admin password
  const [adminEmail,    setAdminEmail]    = useState('admin@consola.openbackup.ar');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminConfirm,  setAdminConfirm]  = useState('');

  // Step 2 — Wasabi
  const [wasabiKey,    setWasabiKey]    = useState('');
  const [wasabiSecret, setWasabiSecret] = useState('');
  const [wasabiEndpoint, setWasabiEndpoint] = useState('https://s3.us-east-1.wasabisys.com');
  const [wasabiRegion, setWasabiRegion] = useState('us-east-1');
  const [wasabiPrefix, setWasabiPrefix] = useState('openbackup-');
  const [wasabiTested, setWasabiTested] = useState(false);

  // Step 3 — company
  const [companyName,    setCompanyName]    = useState('');
  const [companyContact, setCompanyContact] = useState('');
  const [companyEmail,   setCompanyEmail]   = useState('');

  // Step 4 — plan
  const [planName,      setPlanName]      = useState('Starter');
  const [planStorage,   setPlanStorage]   = useState('1000');
  const [planRetention, setPlanRetention] = useState('30');
  const [planPrice,     setPlanPrice]     = useState('0');

  // Step 5 — telegram
  const [tgToken,  setTgToken]  = useState('');
  const [tgChatId, setTgChatId] = useState('');
  const [skipTg,   setSkipTg]   = useState(false);

  const showAlert = (type, msg) => setAlert({ type, msg });
  const clearAlert = () => setAlert(null);

  // -------------------------------------------------------------------------
  // Step 0: Login con admin/admin para obtener el wizard token
  // -------------------------------------------------------------------------
  const handleGetToken = async () => {
    setBusy(true); clearAlert();
    try {
      const data = await api.login('admin', 'admin');
      if (!data.first_run || !data.access_token) {
        showAlert('error', 'No se pudo iniciar el wizard. Recargá la página.');
        return;
      }
      setWizardToken(data.access_token);
      setStep(1);
    } catch (e) {
      showAlert('error', e.message);
    } finally {
      setBusy(false);
    }
  };

  // -------------------------------------------------------------------------
  // Step 1: Crear usuario admin
  // -------------------------------------------------------------------------
  const handleSetAdmin = async () => {
    if (!adminEmail || !adminPassword) { showAlert('error', 'Completá email y contraseña'); return; }
    if (adminPassword !== adminConfirm)  { showAlert('error', 'Las contraseñas no coinciden'); return; }
    if (adminPassword.length < 8)        { showAlert('error', 'Mínimo 8 caracteres'); return; }
    setBusy(true); clearAlert();
    try {
      await api.wizardSetAdminPassword(wizardToken, adminEmail, adminPassword);
      setStep(2);
    } catch (e) {
      showAlert('error', e.message);
    } finally {
      setBusy(false);
    }
  };

  // -------------------------------------------------------------------------
  // Step 2: Wasabi — probar y guardar
  // -------------------------------------------------------------------------
  const wasabiData = () => ({
    access_key:    wasabiKey,
    secret_key:    wasabiSecret,
    endpoint:      wasabiEndpoint,
    region:        wasabiRegion,
    bucket_prefix: wasabiPrefix,
  });

  const handleTestWasabi = async () => {
    if (!wasabiKey || !wasabiSecret) { showAlert('error', 'Ingresá Access Key y Secret Key'); return; }
    setBusy(true); clearAlert();
    try {
      const r = await api.wizardTestWasabi(wizardToken, wasabiData());
      if (r.ok) { showAlert('success', r.msg); setWasabiTested(true); }
      else       showAlert('error', r.msg);
    } catch (e) {
      showAlert('error', e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleSaveWasabi = async () => {
    if (!wasabiKey || !wasabiSecret) { showAlert('error', 'Ingresá Access Key y Secret Key'); return; }
    setBusy(true); clearAlert();
    try {
      await api.wizardSaveWasabi(wizardToken, wasabiData());
      setStep(3);
    } catch (e) {
      showAlert('error', e.message);
    } finally {
      setBusy(false);
    }
  };

  // -------------------------------------------------------------------------
  // Step 3: Empresa interna
  // -------------------------------------------------------------------------
  const handleSetupCompany = async () => {
    if (!companyName || !companyContact || !companyEmail) { showAlert('error', 'Completá todos los campos'); return; }
    setBusy(true); clearAlert();
    try {
      await api.wizardSetupInternalCompany(wizardToken, { name: companyName, contact_name: companyContact, contact_email: companyEmail });
      setStep(4);
    } catch (e) {
      showAlert('error', e.message);
    } finally {
      setBusy(false);
    }
  };

  // -------------------------------------------------------------------------
  // Step 4: Plan inicial
  // -------------------------------------------------------------------------
  const handleCreatePlan = async () => {
    if (!planName) { showAlert('error', 'Ingresá un nombre para el plan'); return; }
    setBusy(true); clearAlert();
    try {
      await api.wizardCreatePlan(wizardToken, {
        name:           planName,
        storage_gb:     parseInt(planStorage) || 1000,
        retention_days: parseInt(planRetention) || 30,
        price_usd:      parseFloat(planPrice) || 0,
      });
      setStep(5);
    } catch (e) {
      showAlert('error', e.message);
    } finally {
      setBusy(false);
    }
  };

  // -------------------------------------------------------------------------
  // Step 5: Telegram (opcional)
  // -------------------------------------------------------------------------
  const handleSaveTelegram = async () => {
    if (skipTg) { setStep(6); return; }
    if (!tgToken || !tgChatId) { showAlert('error', 'Ingresá Bot Token y Chat ID, o saltá este paso'); return; }
    setBusy(true); clearAlert();
    try {
      await api.wizardSaveTelegram(wizardToken, { bot_token: tgToken, chat_id: tgChatId });
      setStep(6);
    } catch (e) {
      showAlert('error', e.message);
    } finally {
      setBusy(false);
    }
  };

  // -------------------------------------------------------------------------
  // Step 6: Completar
  // -------------------------------------------------------------------------
  const handleComplete = async () => {
    setBusy(true); clearAlert();
    try {
      const data = await api.wizardComplete(wizardToken);
      localStorage.setItem('token', data.access_token);
      onComplete(data);
    } catch (e) {
      showAlert('error', e.message);
    } finally {
      setBusy(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  const progress = step === 0 ? 0 : Math.round((step / STEPS.length) * 100);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 520 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}><Logo size={22} /></div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>CONFIGURACIÓN INICIAL</div>
        </div>

        {/* Progress bar */}
        {step > 0 && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              {STEPS.map((s, i) => (
                <div key={s.key} style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: i + 1 <= step ? 'var(--accent)' : 'var(--muted)', textAlign: 'center', flex: 1 }}>
                  {s.num}
                </div>
              ))}
            </div>
            <div style={{ height: 3, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress}%`, background: 'var(--accent)', transition: 'width 0.4s ease' }} />
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--muted)', marginTop: 6, textAlign: 'center', letterSpacing: 1 }}>
              PASO {step} DE {STEPS.length} — {STEPS[step - 1]?.label?.toUpperCase()}
            </div>
          </div>
        )}

        {/* Card */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 28 }}>
          {alert && <Alert type={alert.type} msg={alert.msg} />}

          {/* Step 0: Bienvenida */}
          {step === 0 && (
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 14, marginBottom: 12 }}>Bienvenido</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 20 }}>
                Esta es la primera vez que iniciás la plataforma. El wizard te va a guiar para configurar
                el usuario administrador, el almacenamiento en Wasabi, la empresa interna y las notificaciones.
              </div>
              <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 24 }}>
                El proceso tarda menos de 5 minutos.
              </div>
              <Btn onClick={handleGetToken} loading={busy}>COMENZAR</Btn>
            </div>
          )}

          {/* Step 1: Admin password */}
          {step === 1 && (
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)', marginBottom: 16, letterSpacing: 1 }}>PASO 1 — USUARIO ADMINISTRADOR</div>
              <Field label="Email del administrador" value={adminEmail} onChange={setAdminEmail} placeholder="admin@empresa.com" />
              <Field label="Contraseña" type="password" value={adminPassword} onChange={setAdminPassword} placeholder="Mínimo 8 caracteres" />
              <Field label="Confirmar contraseña" type="password" value={adminConfirm} onChange={setAdminConfirm} placeholder="Repetir contraseña" />
              <Btn onClick={handleSetAdmin} loading={busy}>SIGUIENTE</Btn>
            </div>
          )}

          {/* Step 2: Wasabi */}
          {step === 2 && (
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)', marginBottom: 16, letterSpacing: 1 }}>PASO 2 — ALMACENAMIENTO WASABI</div>
              <Field label="Access Key ID" value={wasabiKey} onChange={v => { setWasabiKey(v); setWasabiTested(false); }} placeholder="AKI..." />
              <Field label="Secret Access Key" type="password" value={wasabiSecret} onChange={v => { setWasabiSecret(v); setWasabiTested(false); }} placeholder="..." />
              <Field label="Endpoint" value={wasabiEndpoint} onChange={setWasabiEndpoint} />
              <Field label="Región" value={wasabiRegion} onChange={setWasabiRegion} />
              <Field label="Prefijo de buckets" value={wasabiPrefix} onChange={setWasabiPrefix} placeholder="openbackup-" />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Btn onClick={handleTestWasabi} loading={busy} secondary>PROBAR CONEXIÓN</Btn>
                <Btn onClick={handleSaveWasabi} loading={busy} disabled={!wasabiTested}>GUARDAR Y CONTINUAR</Btn>
              </div>
              {!wasabiTested && (
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
                  Probá la conexión antes de continuar.
                </div>
              )}
            </div>
          )}

          {/* Step 3: Empresa interna */}
          {step === 3 && (
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)', marginBottom: 16, letterSpacing: 1 }}>PASO 3 — EMPRESA INTERNA</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>
                Este es el cliente interno de la plataforma — tu propia empresa. Se utiliza para gestionar los servidores propios.
              </div>
              <Field label="Nombre de la empresa" value={companyName} onChange={setCompanyName} placeholder="Ej: Mi Empresa S.A." />
              <Field label="Nombre del contacto" value={companyContact} onChange={setCompanyContact} placeholder="Ej: Administrador" />
              <Field label="Email de contacto" value={companyEmail} onChange={setCompanyEmail} placeholder="info@empresa.com" />
              <Btn onClick={handleSetupCompany} loading={busy}>SIGUIENTE</Btn>
            </div>
          )}

          {/* Step 4: Plan inicial */}
          {step === 4 && (
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)', marginBottom: 16, letterSpacing: 1 }}>PASO 4 — PLAN DE BACKUP INICIAL</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>
                Creá el primer plan de backup. Podés agregar más desde Configuración después.
              </div>
              <Field label="Nombre del plan" value={planName} onChange={setPlanName} placeholder="Ej: Starter" />
              <Field label="Almacenamiento (GB)" value={planStorage} onChange={setPlanStorage} placeholder="1000" />
              <Field label="Retención (días)" value={planRetention} onChange={setPlanRetention} placeholder="30" />
              <Field label="Precio (USD/mes)" value={planPrice} onChange={setPlanPrice} placeholder="0" />
              <Btn onClick={handleCreatePlan} loading={busy}>SIGUIENTE</Btn>
            </div>
          )}

          {/* Step 5: Telegram */}
          {step === 5 && (
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)', marginBottom: 16, letterSpacing: 1 }}>PASO 5 — NOTIFICACIONES TELEGRAM</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>
                Opcional. Configurá un bot de Telegram para recibir alertas de backups fallidos y eventos importantes.
              </div>
              {!skipTg && (
                <>
                  <Field label="Bot Token" value={tgToken} onChange={setTgToken} placeholder="123456:ABC..." />
                  <Field label="Chat ID" value={tgChatId} onChange={setTgChatId} placeholder="-100123456789" />
                </>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <Btn onClick={handleSaveTelegram} loading={busy}>{skipTg ? 'SALTAR' : 'GUARDAR Y CONTINUAR'}</Btn>
                {!skipTg && (
                  <button onClick={() => { setSkipTg(true); clearAlert(); }}
                    style={{ background: 'transparent', border: 'none', color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
                    saltar este paso
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 6: Completar */}
          {step === 6 && (
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)', marginBottom: 16, letterSpacing: 1 }}>CONFIGURACIÓN COMPLETA</div>
              <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.8, marginBottom: 8 }}>
                ✓ Usuario administrador creado<br/>
                ✓ Almacenamiento Wasabi configurado<br/>
                ✓ Empresa interna registrada<br/>
                ✓ Plan de backup creado<br/>
                {skipTg ? '— Telegram (no configurado)' : '✓ Notificaciones Telegram configuradas'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 24 }}>
                Todo listo. Hacé clic en Ingresar para comenzar a usar la plataforma.
              </div>
              <Btn onClick={handleComplete} loading={busy}>INGRESAR A LA PLATAFORMA</Btn>
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--muted)', letterSpacing: 1 }}>
          OPENBACKUP — SETUP WIZARD
        </div>
      </div>
    </div>
  );
}
